import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import Sidebar from "../components/admin/Sidebar";
import Header from "../components/Header";
import MobileHeader from "../components/MobileHeader";
import Footer from "../components/Footer";
import Dashboard from "../components/admin/Dashboard";
import Trips from "../components/admin/Trips";
import Inquiries from "../components/admin/Inquiries";
import Settings from "../components/admin/Settings";
import Reviews from "../components/admin/Reviews";
import Category from "../components/admin/Category"; // <-- Add this import
import ImageUpdate from "../components/admin/ImageUpdate"; // <-- Add this import
import "../styles/admin.css"; // Import the CSS file

const AdminPage = () => {
  const [currentSection, setCurrentSection] = useState("dashboard");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Check authentication state on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    
    // Cleanup subscription
    return () => unsubscribe();
  }, []);
  
  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Reset form fields after successful login
      setEmail("");
      setPassword("");
    } catch (error) {
      // console.error("Login error:", error);
      
      switch (error.code) {
        case "auth/invalid-email":
          setError("Invalid email address format.");
          break;
        case "auth/user-disabled":
          setError("This account has been disabled.");
          break;
        case "auth/user-not-found":
          setError("No account found with this email.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password.");
          break;
        default:
          setError("Login failed. Please try again.");
      }
    } finally {
      setLoginLoading(false);
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      // console.error("Logout error:", error);
    }
  };
  
  // Toggle sidebar visibility
  const toggleSidebar = () => {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    sidebar.classList.toggle('show');
    overlay.classList.toggle('active');
  };
  
  // Close sidebar 
  const closeSidebar = () => {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    sidebar.classList.remove('show');
    overlay.classList.remove('active');
  };

  // Switch between different admin sections
  const navigateTo = (section) => {
    setCurrentSection(section);
    closeSidebar();
  };

  // Render the current section component
  const renderSection = () => {
    switch (currentSection) {
      case "dashboard":
        return <Dashboard navigateTo={navigateTo} />;
      case "trips":
        return <Trips />;
      case "inquiries":
        return <Inquiries />;
      case "reviews":
        return <Reviews />;
      case "settings":
        return <Settings />;
      case "categories":
        return <Category />;
      case "updateImages":
        return <ImageUpdate />; // <-- Add this line
      default:
        return <Dashboard navigateTo={navigateTo} />;
    }
  };

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="admin-page-container">
        <div className="d-none d-lg-block">
          <Header />
        </div>
        <MobileHeader />
        <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If not authenticated, show login form
  if (!user) {
    return (
      <div className="admin-page-container">
        <div className="d-none d-lg-block">
          <Header />
        </div>
        <MobileHeader />
        <div className="login-container">
          <div className="login-card" style={{ maxWidth: '380px', minHeight: '440px' }}>
            <div className="login-header py-4">
              <img 
                src="/assets/images/syw-logo.png" 
                alt="SwayYourWay" 
                className="login-logo" 
                style={{ maxWidth: '100px', marginBottom: '1rem' }}
              />
              <h4 className="mb-1">Admin Login</h4>
            </div>
            <div className="login-body pt-2 pb-4 px-4">
              {error && (
                <div className="alert alert-danger py-2 mb-3" role="alert">
                  <small>{error}</small>
                </div>
              )}
              <form onSubmit={handleLogin} className="compact-form mt-2">
                <div className="mb-3">
                  <label htmlFor="email" className="form-label small mb-2">Email</label>
                  <input 
                    type="email" 
                    className="form-control form-control-sm py-2" 
                    id="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required 
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="password" className="form-label small mb-2">Password</label>
                  <input 
                    type="password" 
                    className="form-control form-control-sm py-2" 
                    id="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn btn-primary w-100 py-2 mt-2"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <span>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Logging in...
                    </span>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If authenticated, show admin dashboard
  return (
    <div className="admin-page-container">
      {/* Website Header */}
      <div className="d-none d-lg-block">
        <Header />
      </div>
      <MobileHeader />
      
      {/* Sidebar Overlay */}
      <div className="sidebar-overlay" onClick={closeSidebar}></div>
      
      <div className="admin-wrapper">
        {/* Sidebar Component - added closeSidebar and logout prop */}
        <Sidebar 
          currentSection={currentSection} 
          navigateTo={navigateTo}
          closeSidebar={closeSidebar}
          onLogout={handleLogout}
          user={user}
        />

        {/* Main Content */}
        <main className="main-content">
          {/* Admin Section Title */}
          <div className="admin-section-title">
            <div className="d-flex align-items-center flex-wrap">
              <span className="menu-toggle" onClick={toggleSidebar}>
                <i className="fas fa-bars"></i>
              </span>
              <h2 className="text-break">{currentSection.charAt(0).toUpperCase() + currentSection.slice(1)}</h2>
            </div>
            <div>
              <span className="me-2">{user.email}</span>
              <button onClick={handleLogout} className="btn btn-sm btn-outline-secondary">
                <i className="fas fa-sign-out-alt me-1"></i> Logout
              </button>
            </div>
          </div>

          {/* Render the current section */}
          <div className="section-container">
            {renderSection()}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPage;
